import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Page from '../../../components/page';
import {
  fetchMachineById,
  selectIsFetchingMachine,
  selectMachine,
} from '../../../slices/machines';
import { useNavigate, useParams } from 'react-router';
import {
  Button,
  Drawer,
  Empty,
  Flex,
  Image,
  Input,
  Popover,
  Progress,
  Space,
  Spin,
  Typography,
} from 'antd';
import BasicInfo from './basicInfo';
import {
  BackButton,
  Content,
  DiagnoseTitle,
  Header,
  IssueCard,
  IssueCardImageContainer,
  IssueContainer,
  IssueDescription,
  IssueName,
  Percentage,
  SectionTitle,
  SelectedIssueContainer,
} from './styles';
import { SVGArrowLeft, SVGArrowRight, SVGShare } from '../../../images';
import Icon, {
  CheckOutlined,
  CloseOutlined,
  PictureOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import PossibleCauses, { PossibleCause } from './possibleCauses';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store';
import {
  fetchIssues,
  searchIssues,
  searchIssuesByImage,
  SearchType,
  selectCurrentIssues,
  selectIsFetchingIssues,
  selectIsSearchingIssuesByImage,
  selectIssuesSearchType,
  selectSearchedIssues,
  selectSearchTime,
  setIssues,
} from '../../../slices/issues';
import {
  fetchCauses,
  selectCauses,
  selectIsFetchingCauses,
  setCauses,
} from '../../../slices/causes';
import {
  Issue,
  QueryType,
  UploadBusinessType,
  UploadFileType,
} from '../../../types';
import { useTheme } from 'styled-components';
import ResultIcon from './resultIcon';
import {
  createDraftTemplate,
  fetchTemplateById,
  fetchTemplateRoutines,
  selectDraftTemplate,
  setTemplateCauses,
} from '../../../slices/templates';
import { diagnoseByIssue } from '../../../utils/httpRequest';
import {
  fetchSolutionGrid,
  selectActiveSolutionGrids,
  selectIsFetchingSolutionGrid,
} from '../../../slices/solutionGrids';
import QueryDrawer from './SuggestionDrawer';
import _ from 'lodash';
import MachineDetails from '../../machine/details/info';
import FileUpload from '../../../components/fileUpload';
import {
  closeDiagnoseConversation,
  initDiagnoseConversation,
  selectConversationTemplateId,
  selectIssueEvent,
  setDiagnoseTemplateId,
} from '../../../slices/event';
import { EventInput1 } from '../../../types/events';
import IssueForm from '../../../components/issueForm';

export interface IdentifiedIssue {
  issue: Issue;
  selected: 0 | 1;
}

const DiagnoseMachine = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const machine = useSelector(selectMachine);
  const allIssues = useSelector(selectCurrentIssues);
  const searchedIssues = useSelector(selectSearchedIssues, shallowEqual);
  const searchTime = useSelector(selectSearchTime);
  const causes = useSelector(selectCauses);
  const template = useSelector(selectDraftTemplate);
  const [identifiedIssues, setIdentifiedIssues] = useState<IdentifiedIssue[]>(
    []
  );
  const [possibleCauses, setPossibleCauses] = useState<PossibleCause[]>([]);
  const [queryDrawerVisible, setQueryDrawerVisible] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [search, setSearch] = useState('');
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showIssueDetailsDrawer, setShowIssueDetailsDrawer] = useState(false);
  const [selectedIssueForDetails, setSelectedIssueForDetails] =
    useState<Issue | null>(null);

  const [searchedImage, setSearchedImage] = useState<any>();

  const prevPossibleCauses = useRef<PossibleCause[][]>([]);

  const isFetchingMachine = useSelector(selectIsFetchingMachine);
  const isFetchingCause = useSelector(selectIsFetchingCauses);
  const isFetchingIssue = useSelector(selectIsFetchingIssues);
  const isSearchingIssueByImage = useSelector(selectIsSearchingIssuesByImage);
  const isFetchingSolutionGrid = useSelector(selectIsFetchingSolutionGrid);
  const activeSolutionGrids = useSelector(selectActiveSolutionGrids);

  const issueSearchType = useSelector(selectIssuesSearchType);
  const conversationTemplateId = useSelector(selectConversationTemplateId);

  const issues =
    allIssues.filter(
      (issue) =>
        !!activeSolutionGrids.find((i) => i.issueID === String(issue.issueID))
    ) || [];

  const visibleIssue = issues.filter((issue) =>
    searchedIssues.find((i) => String(i.issueID) === String(issue.issueID))
  );

  const isLoading =
    isFetchingMachine ||
    isFetchingCause ||
    isFetchingIssue ||
    isFetchingSolutionGrid;

  const unIdentifiedIssues = visibleIssue?.filter(
    (issue) =>
      !identifiedIssues.find(
        (identifiedIssue) => identifiedIssue.issue.issueID === issue.issueID
      )
  );

  const currentIssue = unIdentifiedIssues?.[0];

  const handleSearch = (value: string) => {
    setSearch(value);
    setSearchedImage(null);
    dispatch(
      searchIssues({
        params: { search: value },
      })
    );
  };

  const handleDiagnoseByIssues = async (
    issuesForDiagnose: IdentifiedIssue[],
    isSearchedByImage?: boolean
  ) => {
    try {
      if (!isSearchedByImage && diagnosing) {
        return null;
      }
      setDiagnosing(true);
      const identifiedIssuesForDiagnose = issuesForDiagnose || identifiedIssues;

      const res: PossibleCause[] = await diagnoseByIssue({
        templateID: machine?.templateID,
        issues: issues.map(({ issueID }) => ({
          issueID,
          selected:
            identifiedIssuesForDiagnose.find((i) => i.issue.issueID === issueID)
              ?.selected || 0,
        })),
      });
      const possibleCauseCount =
        identifiedIssuesForDiagnose.length === issues.length - 1 ? 1 : 5;

      const allPossibleCauses = res
        .filter((i) => i.ratio > 0.01)
        .sort((a, b) => b.ratio - a.ratio);

      const targetCause = allPossibleCauses.find((i) => i.ratio >= 1);

      const topPossibleCauses = (
        targetCause
          ? [targetCause]
          : allPossibleCauses.splice(0, possibleCauseCount)
      ).map((pc) => ({
        ...pc,
        ...(causes.find(({ causeID }) => causeID === String(pc.causeID)) || {
          name: pc.causeName,
        }),
      }));

      setPossibleCauses(topPossibleCauses);

      if (isSearchedByImage) {
        setIdentifiedIssues(issuesForDiagnose);
      }

      return targetCause;
    } catch (err) {
      console.log(err);
      return null;
    } finally {
      setDiagnosing(false);
    }
  };

  const handleIdentifyIssue = async (selected: 0 | 1) => {
    try {
      dispatch(selectIssueEvent({ issueId: currentIssue.issueID, selected }));
      const targetCause = await handleDiagnoseByIssues(identifiedIssues);

      const nextIdentifiedIssues = [
        { issue: currentIssue, selected },
        ...identifiedIssues,
        ...(targetCause
          ? unIdentifiedIssues
              .splice(1, unIdentifiedIssues.length - 1)
              .map((i) => ({
                issue: i,
                selected: 0 as 0 | 1,
              }))
          : []),
      ];
      prevPossibleCauses.current = prevPossibleCauses.current.concat([
        possibleCauses,
      ]);
      setIdentifiedIssues(nextIdentifiedIssues);
    } catch (err) {
      console.log(err);
    }
  };

  const handleMoreInfoClick = () => {
    // navigate(`/knowledge/machine/${id}`);
    setShowDetailsDrawer(true);
  };

  const handleIssueCardClick = (issue: Issue) => {
    setSelectedIssueForDetails(issue);
    setShowIssueDetailsDrawer(true);
  };

  const handleNextClick = () => {
    handleIdentifyIssue(0);
  };

  const handlePrevClick = () => {
    const nextIdentifiedIssues = _.clone(identifiedIssues);
    nextIdentifiedIssues.shift();
    setIdentifiedIssues(nextIdentifiedIssues);
    const nextPossibleCauses = prevPossibleCauses.current.pop() || [];
    setPossibleCauses(nextPossibleCauses);
  };

  const handleSearchIssueByImage = async (response: any) => {
    const files = response?.data;
    setSearchedImage(files[0]);
    dispatch(
      searchIssuesByImage({
        templateId: String(machine?.templateID),
        fileId: files[0].id,
      })
    );
  };

  useEffect(() => {
    if (issueSearchType === SearchType.IMAGE) {
      if (!searchedIssues.length) {
        setDiagnosing(false);
        return;
      }
      const selectedIssues: IdentifiedIssue[] = searchedIssues.map((i) => ({
        issue: i,
        selected: 1,
      }));
      const unSelectedIssues: IdentifiedIssue[] = issues
        .filter((i) => !searchedIssues.find((si) => si.issueID === i.issueID))
        .map((i) => ({
          issue: i,
          selected: 0,
        }));
      handleDiagnoseByIssues([...selectedIssues, ...unSelectedIssues], true);
    }
  }, [issueSearchType, searchedIssues, searchTime]);

  useEffect(() => {
    if (id) {
      dispatch(fetchMachineById(id));
    }
  }, [id]);

  useEffect(() => {
    if (machine?.templateID) {
      dispatch(fetchTemplateById(machine?.templateID));
      dispatch(fetchTemplateRoutines({ templateId: machine?.templateID }));
      dispatch(fetchIssues({ templateID: machine?.templateID }));
      dispatch(fetchCauses({ templateID: machine?.templateID }));
      dispatch(fetchSolutionGrid({ templateID: machine?.templateID }));
      if (conversationTemplateId !== machine.templateID) {
        dispatch(setDiagnoseTemplateId(machine.templateID));
        dispatch(initDiagnoseConversation(machine.templateID));
      }
    }
  }, [machine?.templateID]);

  useEffect(() => {
    dispatch(setTemplateCauses({ causes }));
  }, [causes]);

  useEffect(() => {
    setIdentifiedIssues([]);
    setPossibleCauses([]);
    setDiagnosing(false);
  }, [searchedIssues]);

  useEffect(() => {
    setSearch('');
    setSearchedImage(null);
    dispatch(createDraftTemplate());
    dispatch(setIssues([]));
    dispatch(setCauses([]));
    return () => {
      dispatch(
        closeDiagnoseConversation({
          status: EventInput1.ABANDON,
          checkDuplicate: true,
        })
      );
    };
  }, []);

  return (
    <Page
      header={
        <Header>
          <BackButton
            onClick={() => {
              navigate(-1);
            }}
          >
            <SVGArrowLeft style={{ width: 28 }} />
          </BackButton>
          {machine && <BasicInfo machine={machine} template={template} />}
          <Button
            type="primary"
            ghost
            onClick={handleMoreInfoClick}
            icon={<Icon component={SVGShare} />}
          >
            More Info
          </Button>
        </Header>
      }
    >
      {issues.length ? (
        <Content>
          <IssueContainer gap={32}>
            <Flex gap={4}>
              <Flex flex={1} vertical gap={4}>
                <Input.Search
                  placeholder={t('search_for_issue')}
                  allowClear
                  style={{ width: '100%' }}
                  enterButton={<SearchOutlined />}
                  onSearch={handleSearch}
                  onChange={(e) => setSearch(e.target.value)}
                  value={search}
                />
              </Flex>
              <FileUpload
                businessType={UploadBusinessType.ISSUE}
                fileType={UploadFileType.IMAGE}
                onUploaded={handleSearchIssueByImage}
                onUploading={() => setDiagnosing(true)}
              >
                <Button
                  style={{
                    background:
                      'linear-gradient(136deg, #286aff, #4e6ef2, #7274f9, #9f66ff)',
                    color: '#fff',
                    height: '33px',
                  }}
                  icon={<PictureOutlined />}
                >
                  AI Image
                </Button>
              </FileUpload>
            </Flex>
            {visibleIssue.length ? (
              <Flex vertical gap={12}>
                {searchedImage && !currentIssue && (
                  <Flex justify="center">
                    <Image
                      src={searchedImage?.fileUrl}
                      width={80}
                      height={60}
                      fallback="/placeholder.png"
                    />
                  </Flex>
                )}
                <DiagnoseTitle>
                  {currentIssue
                    ? t('identify_cause_hint')
                    : t('diagnose_done_message')}
                </DiagnoseTitle>
                <Progress
                  percent={
                    currentIssue
                      ? (identifiedIssues.length / issues.length) * 100
                      : 100
                  }
                  strokeColor={theme.antd.colorPrimary}
                  trailColor="#dededf"
                  showInfo={false}
                  size={['100%', 12]}
                />
                {!currentIssue && (
                  <Flex
                    align="center"
                    justify="center"
                    vertical
                    gap={12}
                    style={{ marginTop: 28, marginBottom: 28 }}
                  >
                    <Typography.Text>
                      {t('observe_other_issue')}
                    </Typography.Text>
                    <Button
                      type="primary"
                      ghost
                      style={{ backgroundColor: '#fff' }}
                      onClick={() => setQueryDrawerVisible(true)}
                    >
                      {t('suggest_an_issue')}
                    </Button>
                    <QueryDrawer
                      visible={queryDrawerVisible}
                      onClose={() => setQueryDrawerVisible(false)}
                      onConfirm={() => setQueryDrawerVisible(false)}
                      queryType={QueryType.ISSUE}
                    />
                  </Flex>
                )}

                {currentIssue && (
                  <IssueCard>
                    {!!identifiedIssues?.length && (
                      <Button
                        className="arrow-left"
                        icon={<SVGArrowRight style={{ width: 28 }} />}
                        ghost
                        title={t('previous')}
                        onClick={handlePrevClick}
                      />
                    )}
                    <Flex vertical flex={1} gap={8}>
                      <Flex gap={8}>
                        <IssueCardImageContainer
                          onClick={() => handleIssueCardClick(currentIssue)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Image
                            src={currentIssue?.images?.[0]?.url}
                            preview={false}
                            fallback="/placeholder.png"
                          />
                        </IssueCardImageContainer>
                        <Flex vertical flex={1}>
                          <IssueName>{currentIssue.name}</IssueName>
                        </Flex>
                      </Flex>
                      {currentIssue.description && (
                        <IssueDescription>
                          {currentIssue.description}
                        </IssueDescription>
                      )}
                    </Flex>
                    <Space size={10}>
                      <Button
                        onClick={() => {
                          handleIdentifyIssue(0);
                        }}
                      >
                        <CloseOutlined />
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => {
                          handleIdentifyIssue(1);
                        }}
                      >
                        <CheckOutlined />
                      </Button>
                    </Space>
                    <Button
                      className="arrow-right"
                      icon={<SVGArrowRight style={{ width: 28 }} />}
                      ghost
                      title={t('next_issue')}
                      onClick={handleNextClick}
                    />
                  </IssueCard>
                )}
              </Flex>
            ) : (
              <Flex
                align="center"
                justify="center"
                vertical
                gap={12}
                style={{ marginTop: 28, marginBottom: 28 }}
              >
                <Empty
                  description={t('no_matching_issue')}
                  image={
                    issueSearchType === SearchType.IMAGE &&
                    !isSearchingIssueByImage ? (
                      <Image
                        src={searchedImage?.fileUrl}
                        width={100}
                        height={75}
                        fallback="/placeholder.png"
                      />
                    ) : null
                  }
                ></Empty>
              </Flex>
            )}
            {!!identifiedIssues?.length && (
              <SelectedIssueContainer>
                <SectionTitle>
                  {issueSearchType === SearchType.IMAGE
                    ? t('Matched')
                    : t('selected')}{' '}
                  {t('issues')}{' '}
                  <span>
                    (
                    {issueSearchType === SearchType.IMAGE
                      ? searchedIssues?.length
                      : identifiedIssues?.length}
                    )
                  </span>
                </SectionTitle>
                {identifiedIssues?.map(({ issue, selected }) => (
                  <IssueCard key={issue.issueID}>
                    <Flex vertical flex={1} gap={8}>
                      <Flex gap={8}>
                        <IssueCardImageContainer
                          onClick={() => handleIssueCardClick(issue)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Image
                            src={issue?.images?.[0]?.url}
                            preview={false}
                            fallback="/placeholder.png"
                          />
                        </IssueCardImageContainer>
                        <Flex gap={8}>
                          <IssueName>{issue.name}</IssueName>
                          {issueSearchType === SearchType.IMAGE && selected ? (
                            <Popover
                              content={
                                <Flex
                                  vertical
                                  gap={8}
                                  style={{
                                    maxWidth: 540,
                                    maxHeight: 300,
                                    overflowY: 'scroll',
                                  }}
                                >
                                  <Typography.Text strong>
                                    {t('compare_reason')}
                                  </Typography.Text>
                                  <Typography.Text>
                                    {issue.classificationReason}
                                  </Typography.Text>
                                  <Typography.Text strong>
                                    {t('description_for_issues_image')}
                                  </Typography.Text>
                                  {issue?.embeddingDescription?.map((i) => (
                                    <Typography.Text key={i}>
                                      {i}
                                    </Typography.Text>
                                  ))}
                                </Flex>
                              }
                              title={
                                <Flex vertical>
                                  {`Why "${issue.name}" matched?`}
                                  <Flex align="center" gap={8}>
                                    <Typography.Text>
                                      Similarity Score:
                                    </Typography.Text>
                                    <Percentage>
                                      {(
                                        Number(issue.similarityScore) * 100
                                      ).toFixed()}
                                    </Percentage>
                                  </Flex>
                                </Flex>
                              }
                              trigger="hover"
                            >
                              <Typography.Text type="secondary">
                                <Percentage>
                                  {(
                                    Number(issue.similarityScore) * 100
                                  ).toFixed()}
                                </Percentage>
                                {/* <QuestionCircleOutlined /> */}
                              </Typography.Text>
                            </Popover>
                          ) : (
                            ''
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                    <ResultIcon right={!!selected} />
                  </IssueCard>
                ))}
              </SelectedIssueContainer>
            )}
          </IssueContainer>
          <PossibleCauses
            identified={!!visibleIssue.length && !currentIssue}
            causes={possibleCauses}
          />
        </Content>
      ) : (
        !isLoading && (
          <Empty style={{ marginTop: 80 }} description={t('no_issues')} />
        )
      )}
      <Spin
        spinning={isLoading || diagnosing || isSearchingIssueByImage}
        fullscreen
        size="large"
      ></Spin>
      <Drawer
        title={machine?.name}
        open={showDetailsDrawer}
        onClose={() => setShowDetailsDrawer(false)}
        width={800}
      >
        <MachineDetails />
      </Drawer>
      <Drawer
        title={`${t('issue')} - ${selectedIssueForDetails?.name || ''}`}
        open={showIssueDetailsDrawer}
        onClose={() => setShowIssueDetailsDrawer(false)}
        width={800}
        footer={null}
      >
        {selectedIssueForDetails && (
          <IssueForm issue={selectedIssueForDetails as Issue} readOnly />
        )}
      </Drawer>
    </Page>
  );
};

export default DiagnoseMachine;
